import {type FC, useEffect, useRef, useState} from 'react';
import ReactPlayer from 'react-player';
import useReplayStore from '../../../store/useReplayStore.ts';
import PausePNG from '../../../assets/vod/pause.png';
import PlayPNG from '../../../assets/vod/play.png';
import RewindPNG from '../../../assets/vod/rewind.png';
import ForwardPNG from '../../../assets/vod/forward.png';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import {
    CloseButton,
    LayoutButton,
    LayoutItem,
    PlayerContainer,
    PlayerWrapper,
    Progress,
    ProgressBar,
    TimeDisplay,
    VideoContainer,
} from '../types/ReplayPlayer.styled.ts';

const ReplayPlayer: FC = () => {
    const playerRef = useRef<ReactPlayer>(null);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [hideControlsTimeout, setHideControlsTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [playing, setPlaying] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    const {lastFocus, showPlayer, selectedVideoId, handleClosePlayer} = useReplayStore();

    const {ref: playPauseRef, focused: playPauseFocused} = useFocusable({
        focusKey: 'player-control-play-pause',
        onEnterPress: () => togglePlayPause(),
        onArrowPress: (direction) => {
            showControlsTemporarily();
            if (direction === 'up' || direction === 'down') {
                return false;
            }
            return true;
        },
    });

    const {ref: skipBackRef, focused: skipBackFocused} = useFocusable({
        focusKey: 'player-control-skip-back',
        onEnterPress: () => skipTime(-5),
        onArrowPress: (direction) => {
            showControlsTemporarily();
            if (direction === 'up' || direction === 'down') {
                return false;
            }
            if (direction === 'left') {
                setFocus('player-control-close');
                return false;
            }
            return true;
        },
    });

    const {ref: skipForwardRef, focused: skipForwardFocused} = useFocusable({
        focusKey: 'player-control-skip-forward',
        onEnterPress: () => skipTime(5),
        onArrowPress: (direction) => {
            showControlsTemporarily();
            if (direction === 'up' || direction === 'down') {
                return false;
            }
            return true;
        },
    });

    const {ref: closeRef, focused: closeFocused} = useFocusable({
        focusKey: 'player-control-close',
        onEnterPress: () => {
            handleClosePlayer();
        },
        onArrowPress: (direction) => {
            showControlsTemporarily();
            if (direction === 'up' || direction === 'down') {
                return false;
            }
            return true;
        },
    });

    const skipTime = (seconds: number) => {
        if (playerRef.current) {
            const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
            playerRef.current.seekTo(newTime);
            setCurrentTime(newTime);
        }
        showControlsTemporarily();
    };

    const togglePlayPause = () => {
        setPlaying(!playing);
        showControlsTemporarily();
    };

    const showControlsTemporarily = () => {
        setControlsVisible(true);
        if (hideControlsTimeout) {
            clearTimeout(hideControlsTimeout);
        }
        const timeout = setTimeout(() => {
            setControlsVisible(false);
        }, 5000);
        setHideControlsTimeout(timeout);
    };

    const handleMouseMove = () => {
        showControlsTemporarily();
    };

    const handleProgress = (progressData: {played: number; playedSeconds: number}) => {
        setProgress(progressData.played * 100);
        setCurrentTime(progressData.playedSeconds);
    };

    const handleDuration = (durationSeconds: number) => {
        setDuration(durationSeconds);
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (showPlayer) {
            showControlsTemporarily();
            setPlaying(true);
            setTimeout(() => {
                setFocus('player-control-close');
            }, 100);
        } else {
            if (hideControlsTimeout) {
                clearTimeout(hideControlsTimeout);
            }
            setPlaying(false);
        }
        return () => {
            setFocus(lastFocus);
            if (hideControlsTimeout) {
                clearTimeout(hideControlsTimeout);
            }
        };
    }, [showPlayer]);

    const videoUrl = `https://www.youtube.com/watch?v=${selectedVideoId}`;

    return (
        <PlayerContainer $isVisible={showPlayer} onMouseMove={handleMouseMove}>
            <VideoContainer>
                <PlayerWrapper>
                    <ReactPlayer
                        ref={playerRef}
                        url={videoUrl}
                        playing={playing}
                        controls={false}
                        width="100%"
                        height="100%"
                        onProgress={handleProgress}
                        onDuration={handleDuration}
                        onReady={() => setPlaying(true)}
                        config={{
                            youtube: {
                                playerVars: {
                                    autoplay: 1,
                                    controls: 0,
                                    disablekb: 1,
                                    fs: 0,
                                    iv_load_policy: 3,
                                    modestbranding: 1,
                                    rel: 0,
                                    showinfo: 0,
                                },
                            },
                        }}
                    />
                </PlayerWrapper>

                {controlsVisible && (
                    <>
                        <TimeDisplay>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </TimeDisplay>

                        <ProgressBar>
                            <Progress $progress={progress} />
                        </ProgressBar>
                    </>
                )}

                <CloseButton ref={closeRef} $focused={closeFocused} onClick={() => handleClosePlayer()}>
                    전체화면 닫기
                </CloseButton>
                <LayoutButton $visible={controlsVisible}>
                    <LayoutItem ref={skipBackRef} $focused={skipBackFocused} onClick={() => skipTime(-5)}>
                        <img src={RewindPNG} alt={'rewind'} />
                    </LayoutItem>
                    <LayoutItem ref={playPauseRef} $focused={playPauseFocused} onClick={togglePlayPause}>
                        {playing ? <img src={PausePNG} alt={'pause'} /> : <img src={PlayPNG} alt={'play'} />}
                    </LayoutItem>
                    <LayoutItem ref={skipForwardRef} $focused={skipForwardFocused} onClick={() => skipTime(5)}>
                        <img src={ForwardPNG} alt={'forward'} />
                    </LayoutItem>
                </LayoutButton>
            </VideoContainer>
        </PlayerContainer>
    );
};

export default ReplayPlayer;
